import * as React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { Link } from "gatsby";
import { DefaultFontStack } from "../../../typography/typography";
import { StaticImage } from "gatsby-plugin-image";
import PullQuote from "../../../pullquote/pullQuote";

const Root = styled.div(DefaultFontStack, {
  top: 0,
  left: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "rgb(0 0 0 / 50%)",
  height: "100%",
  width: "100%",
  position: "fixed",
  zIndex: 30,
  boxSizing: "border-box",
  padding: "20px",
});
const Content = styled.div({
  height: "fit-content",
  maxHeight: "100%",
  background: "white",
  width: "100%",
  maxWidth: 780,
  overflowY: "auto",
});

const CloseContainer = styled.div({
  backgroundColor: "#075391",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
});

const CloseButton = styled.button({
  appearance: "none",
  border: "none",
  outline: "none",
  backgroundColor: "unset",
  color: "white",
  padding: 0,
  marginRight: 10,
});

const Heading = styled.span(({ theme }) => ({
  display: "block",
  letterSpacing: "0.25px",
  fontWeight: 500,
  fontSize: 19,
  color: theme.colors.awesome,
}));

const Body = styled.span(({ theme }) => ({
  color: theme.colors.highEmphasis,
  letterSpacing: "0.5px",
  fontWeight: 400,
  display: "block",
  margin: "16px 0px 25px 0px",
}));

const ScrollContent = styled.div({
  overflowY: "auto",
});

const DeviceSection = styled.div({
  padding: 40,
});

const OfferRoot = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 15,
  "@media(min-width: 500px)": {
    flexDirection: "row",
  },
});

const WeProvideRoot = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  "> span": {
    color: "#999",
    fontSize: 14,
    ":first-of-type": {
      fontSize: 16,
      color: theme.colors.highEmphasis,
      fontWeight: 600,
      marginBottom: 10,
    },
  },
}));

const YouProvideRoot = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  "> span": {
    color: "#999",
    fontSize: 14,
    ":first-of-type": {
      fontSize: 16,
      color: theme.colors.highEmphasis,
      fontWeight: 600,
      marginBottom: 10,
    },
  },
}));

const UpAndRunningSection = styled.div(({ theme }) => ({
  padding: "40px 40px 15px 40px",
  backgroundColor: theme.colors.cloud,
}));

const InstallationSection = styled.div({
  padding: 40,
});

const Heading2 = styled.span(({ theme }) => ({
  display: "block",
  color: theme.colors.highEmphasis,
  fontWeight: 600,
}));

const Grid = styled.div({
  display: "grid",
  marginTop: 40,
  gap: 35,
  "@media(min-width: 500px)": {
    gridTemplateColumns: "auto auto",
    gap: 50,
  },
});

const InstallStep = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "> span": {
    ":first-of-type": {
      display: "block",
      letterSpacing: "0.25px",
      fontWeight: 500,
      fontSize: 16,
      color: theme.colors.awesome,
    },
    ":nth-of-type(2)": {
      display: "block",
      color: theme.colors.highEmphasis,
      letterSpacing: "0.5px",
      fontWeight: 400,
      display: "block",
      margin: "16px 0px 25px 0px",
      lineHeight: "23px",
    },
  },
}));

const DataSection = styled.div(({ theme }) => ({
  padding: 40,
  backgroundColor: theme.colors.ice,
}));

const List = styled.ul(({ theme }) => ({
  listStyle: "none",
  display: "flex",
  gap: 16,
  flexDirection: "column",
  li: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
    color: theme.colors.highEmphasis,
    letterSpacing: "0.5px",
    fontWeight: 400,
  },
}));

const QuoteSection = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: 40,
});

const QuoteContainer = styled.div(({ theme }) => ({}));

const FreeTrialLink = styled(Link)(({ theme }) => ({
  display: "block",
  width: "fit-content",
  backgroundColor: theme.colors.awesome,
  color: theme.colors.day,
  textDecoration: "none",
  fontFamily: "Arial",
  fontSize: 16,
  padding: "10px 15px",

  marginTop: 40,
  letterSpacing: "1.5px",
}));

const ImageGrid = styled.div({
  display: "grid",
  marginTop: 40,
  gridTemplateAreas: `
    "main"
    "secondary"
    "third"
  `,

  "@media(min-width: 500px)": {
    gridTemplateAreas: `
    "main  main secondary"
    "main main third"
  `,
  },
});

function Dialog(props) {
  const { onClose } = props;
  return (
    <Root>
      <Content>
        <CloseContainer>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faClose} style={{ height: 20, width: 20 }} />
          </CloseButton>
        </CloseContainer>
        <ScrollContent>
          <DeviceSection>
            <Heading>THIS IS AN XL DEVICE</Heading>
            <Body>
              It is a{" "}
              <strong>
                sophisticated all-in-one device that integrates a production
                monitor, built-in database, powerful browser-based reporting
                suite, and a plant-floor display
              </strong>{" "}
              (XL810) or HDMI output (XL HD). It contains everything you need to
              monitor and improve production.
            </Body>
            <Body>
              What’s more, it{" "}
              <strong>couldn’t be easier to set up and use.</strong> Simply
              connect power, plug in an Ethernet cable, hook up one or two
              sensors, and you’re ready to go - over 100 metrics and 20+ data
              dimensions at your fingertips.
            </Body>
            <OfferRoot>
              <WeProvideRoot>
                <span>We provide</span>
                <span>XL Productivity Appliance</span>
                <span>Power Cord</span>
                <span>XL Barcode Kit (optional)</span>
              </WeProvideRoot>
              <StaticImage
                alt="XL-HD"
                src="../assets/XL HD.png"
                width={310}
                objectFit="contain"
              />
              <YouProvideRoot>
                <span>You provide</span>
                <span>One or two sensors</span>
                <span>Ethernet cable</span>
              </YouProvideRoot>
            </OfferRoot>
            <Heading style={{ marginTop: 40 }}>
              THIS IS WHAT YOUR OPERATORS SEE
            </Heading>
            <Body>
              This is an example of an XL plant-floor display (XL HD). It
              provides your operators with{" "}
              <strong>real- time data so they can benchmark progress</strong>{" "}
              and instantly react to issues during the shift. The scoreboard
              enables operators to take control and win their shift!
            </Body>
            <ImageGrid>
              <StaticImage
                style={{ gridArea: "main", height: "100%" }}
                src="../assets/XL HD Scoreboard Running.png"
                alt="Running Normally"
                objectFit="contain"
              />
              <StaticImage
                style={{ gridArea: "secondary" }}
                src="../assets/XL HD Scoreboard Down.png"
                alt="Down"
                objectFit="contain"
              />
              <StaticImage
                style={{ gridArea: "third" }}
                src="../assets/XL HD Scoreboard Changeover.png"
                alt="Changeover"
                objectFit="contain"
              />
            </ImageGrid>
          </DeviceSection>
          <UpAndRunningSection>
            <Heading>UP AND RUNNING IN LESS THAN A DAY</Heading>
            <Body>
              All you have to do is connect XL to any digital pulse, like a
              photo eye, proximity switch, limit switch, relay, set of dry
              contacts, encoder, or output from a PLC.
            </Body>
          </UpAndRunningSection>
          <InstallationSection>
            <Heading2>INSTALL IN 3 EASY STEPS</Heading2>
            <Grid>
              <StaticImage
                alt="display"
                src="../assets/XL HD Counts.png"
                objectFit="contain"
                height={175}
              />
              <InstallStep>
                <span>1. Connect it to the line</span>
                <span>
                  <strong>
                    To connect XL to your process, simply wire sensors to a
                    terminal on the XL.
                  </strong>
                  You can either use an existing output from a sensor or PLC, or
                  you can add a new one. Typical inputs include a photo eye,
                  proximity switch, limit switch, relay, set of dry contacts,
                  encoder, or output from a PLC.
                </span>
              </InstallStep>
              <StaticImage
                alt="network"
                src="../assets/IT Image XL HD.png"
                height={175}
                objectFit="contain"
              />
              <InstallStep>
                <span>2. Connect it to your network</span>
                <span>
                  <strong>
                    Connect the XL device to your network via Ethernet.
                  </strong>
                  We support DHCP or the use of a static IP address. You don't
                  need to install any software - you can access every report
                  from a browser.
                </span>
              </InstallStep>
              <StaticImage
                alt="network"
                src="../assets/scanner.png"
                height={175}
                objectFit="contain"
              />
              <InstallStep>
                <span>3. Wire the barcode scanner</span>
                <span>
                  <strong>
                    Connect the XL device to your network via Ethernet.
                  </strong>
                  We support DHCP or the use of a static IP address. You don't
                  need to install any software - you can access every report
                  from a browser.
                </span>
              </InstallStep>
            </Grid>
          </InstallationSection>
          <DataSection>
            <Heading>DATA IS STORED WITHIN THE XL DEVICE</Heading>
            <List>
              <li>
                <StaticImage
                  style={{ flexShrink: 0 }}
                  src="../assets/check.png"
                  alt="check"
                  layout="fixed"
                  height={16}
                  width={16}
                  objectFit="contain"
                />
                <span>
                  <strong> Everything you need instantly,</strong> all of your
                  users can access the data as soon as you connect XL to your
                  network.
                </span>
              </li>
              <li>
                <StaticImage
                  style={{ flexShrink: 0 }}
                  src="../assets/check.png"
                  alt="check"
                  layout="fixed"
                  height={16}
                  width={16}
                  objectFit="contain"
                />
                <span>
                  <strong>
                    Each XL device has 4GB on-board to store your data.{" "}
                  </strong>
                </span>
              </li>
              <li>
                <StaticImage
                  style={{ flexShrink: 0 }}
                  src="../assets/check.png"
                  alt="check"
                  layout="fixed"
                  height={16}
                  width={16}
                  objectFit="contain"
                />
                <span>
                  <strong>
                    It's not cloud-based, so there are no IT headaches.
                  </strong>
                  Operates entirely within your firewall, giving you greater IT
                  security.
                </span>
              </li>
              <li>
                <StaticImage
                  style={{ flexShrink: 0 }}
                  src="../assets/check.png"
                  alt="check"
                  layout="fixed"
                  height={16}
                  width={16}
                  objectFit="contain"
                />
                <span>
                  <strong> No software or licenses to install,</strong> no user
                  limit, no support contract, no recurring fees. Technical
                  support is included.
                </span>
              </li>
            </List>
          </DataSection>
          <QuoteSection>
            <QuoteContainer>
              <PullQuote attribution="-C.T. | Lean Improvement Team | Global Imaging Products Manufacturer">
                After three days, our team was producing at 115% of plan and set
                a new production record. At the end of the shift our plant floor
                team broke into applause! That's why every line is getting an
                XL.
              </PullQuote>
            </QuoteContainer>

            <FreeTrialLink onClick={onClose} to="/products/free-trial">
              Try Vorne XL free for 90-days
            </FreeTrialLink>
          </QuoteSection>
          <CloseContainer>
            <CloseButton onClick={onClose}>
              <FontAwesomeIcon
                icon={faClose}
                style={{ height: 20, width: 20 }}
              />
            </CloseButton>
          </CloseContainer>
        </ScrollContent>
      </Content>
    </Root>
  );
}

export default Dialog;
